// components/QueryResultsTable.js

import React, { useState } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import SnackbarAlert from "./SnackbarAlert";

const QueryResultsTable = ({ results }) => {
  const [orderBy, setOrderBy] = useState(null);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (!Array.isArray(results) || results.length === 0) return null;

  const headers = Object.keys(results[0]);

  const handleSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderBy(header);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const sortedResults = [...results].sort((a, b) => {
    if (!orderBy) return 0;
    const aValue = a[orderBy] ?? "";
    const bValue = b[orderBy] ?? "";
    return (aValue < bValue ? -1 : 1) * (orderDirection === "asc" ? 1 : -1);
  });

  const paginatedResults = sortedResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCopy = () => {
    const headerLine = headers.join(", ");
    const rows = results.map((row) =>
      headers.map((h) => JSON.stringify(row[h] ?? "")).join(", ")
    );
    const csvContent = [headerLine, ...rows].join("\n");

    navigator.clipboard.writeText(csvContent).then(() => {
      setSnackbarMessage("Query results copied to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    });
  };

  const handleDownload = () => {
    const headerLine = headers.join(", ");
    const rows = results.map((row) =>
      headers.map((h) => JSON.stringify(row[h] ?? "")).join(", ")
    );
    const csvContent = [headerLine, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "query_results.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper
      style={{
        backgroundColor: "#1a1a1a",
        color: "white",
        maxHeight: 400,
        width: "60%",
        overflow: "auto",
        marginTop: "1%",
        fontFamily: "Raleway, sans-serif",
      }}
    >
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  sortDirection={orderBy === header ? orderDirection : false}
                  sx={{
                    color: "#FFC629",
                    backgroundColor: "#111",
                    fontFamily: "Raleway, sans-serif",
                  }}
                >
                  <TableSortLabel
                    active={orderBy === header}
                    direction={orderBy === header ? orderDirection : "asc"}
                    onClick={() => handleSort(header)}
                    style={{
                      color: "#FFC629",
                      "&:hover": { color: "#FFC629" },
                    }}
                  >
                    {header}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell
                sx={{ width: "15%", backgroundColor: "#111", color: "#FFC629", textAlign: "right" }}
              >
                <Tooltip title="Copy as CSV">
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon
                      fontSize="small"
                      sx={{ color: "#FFC629" }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download CSV">
                  <IconButton size="small" onClick={handleDownload}>
                    <DownloadIcon fontSize="small" sx={{ color: "#FFC629" }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedResults.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    style={{
                      color: "white",
                      borderBottom: "1px solid #333",
                      fontFamily: "Raleway, sans-serif",
                    }}
                  >
                    {row[header] !== null && row[header] !== undefined
                      ? row[header].toString()
                      : ""}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={results.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        sx={{
          color: "#FFC629",
          ".MuiTablePagination-toolbar": {
            backgroundColor: "#111",
            fontFamily: "Raleway, sans-serif",
          },
          ".MuiSvgIcon-root": { color: "#FFC629" },
          ".MuiTablePagination-displayedRows": {
            fontFamily: "Raleway, sans-serif",
          },
        }}
      />
      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />
    </Paper>
  );
};

export default QueryResultsTable;
